// src/pages/DashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  ShoppingBag, 
  TrendingUp, 
  Users,
  Package,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { reportApi, productApi } from '../api';

const DashboardPage = () => {
  const { language } = useApp();
  const t = translations[language];
  
  const [todayStats, setTodayStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // 加载今日概览
      const todayRes = await reportApi.getTodayOverview();
      if (todayRes.success) {
        setTodayStats(todayRes.data);
      }

      // 加载畅销商品
      const topRes = await reportApi.getTopProducts({ limit: 5 });
      if (topRes.success) {
        setTopProducts(topRes.data || []);
      }

      // 加载低库存商品
      const lowStockRes = await productApi.getLowStock();
      if (lowStockRes.success) {
        setLowStockProducts(lowStockRes.data || []);
      }
    } catch (err) {
      console.error('加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (product) => {
    switch (language) {
      case 'en':
        return product.name_en || product.name;
      case 'km':
        return product.name_km || product.name;
      default:
        return product.name;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">
            {language === 'en' ? 'Loading...' :
             language === 'km' ? 'កំពុងផ្ទុក...' :
             '加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">
            {t.dashboard || (language === 'en' ? 'Dashboard' : language === 'km' ? 'ផ្ទាំងគ្រប់គ្រង' : '数据看板')}
          </h2>
          <p className="text-gray-600 mt-1">
            {language === 'en' ? 'Overview of your business performance' :
             language === 'km' ? 'ទិដ្ឋភាពទូទៅនៃការអនុវត្តអាជីវកម្មរបស់អ្នក' :
             '查看您的业务表现概览'}
          </p>
        </div>
        <div className="flex items-center space-x-2 text-gray-600">
          <Calendar className="w-5 h-5" />
          <span>{new Date().toLocaleDateString(language === 'km' ? 'km-KH' : language, { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      </div>

      {/* 今日统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Today Sales' :
                 language === 'km' ? 'ការលក់ថ្ងៃនេះ' :
                 '今日销售额'}
              </p>
              <p className="text-3xl font-bold mt-2">
                ${parseFloat(todayStats?.total_sales || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>
              {language === 'en' ? 'Revenue' :
               language === 'km' ? 'ប្រាក់ចំណូល' :
               '营业收入'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Transactions' :
                 language === 'km' ? 'ប្រតិបត្តិការ' :
                 '交易数量'}
              </p>
              <p className="text-3xl font-bold mt-2">
                {todayStats?.transaction_count || 0}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <ShoppingBag className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-600">
            <span>
              {language === 'en' ? 'Orders completed' :
               language === 'km' ? 'ការបញ្ជាទិញបានបញ្ចប់' :
               '已完成订单'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Avg. Transaction' :
                 language === 'km' ? 'ប្រតិបត្តិការជាមធ្យម' :
                 '平均客单价'}
              </p>
              <p className="text-3xl font-bold mt-2">
                ${parseFloat(todayStats?.avg_transaction || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-600">
            <span>
              {language === 'en' ? 'Per order' :
               language === 'km' ? 'ក្នុងមួយការបញ្ជាទិញ' :
               '每笔订单'}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">
                {language === 'en' ? 'Items Sold' :
                 language === 'km' ? 'ទំនិញបានលក់' :
                 '售出商品'}
              </p>
              <p className="text-3xl font-bold mt-2">
                {todayStats?.items_sold || 0}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <Package className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-gray-600">
            <span>
              {language === 'en' ? 'Total quantity' :
               language === 'km' ? 'បរិមាណសរុប' :
               '总数量'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 畅销商品 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-bold">
              {language === 'en' ? 'Top Selling Products' :
               language === 'km' ? 'ផលិតផលលក់ដាច់បំផុត' :
               '畅销商品'}
            </h3>
          </div>
          <div className="p-6">
            {topProducts.length > 0 ? (
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={product.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        index === 0 ? 'bg-yellow-100 text-yellow-600' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-600' :
                        'bg-gray-50 text-gray-500'
                      } font-bold text-sm`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{getProductName(product)}</p>
                        <p className="text-sm text-gray-500">
                          ${parseFloat(product.sale_price || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">
                        {product.total_sold || 0} {language === 'en' ? 'sold' : language === 'km' ? 'លក់' : '已售'}
                      </p>
                      <p className="text-sm text-gray-500">
                        ${parseFloat(product.total_revenue || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'en' ? 'No data' : language === 'km' ? 'គ្មានទិន្នន័យ' : '暂无数据'}</p>
              </div>
            )}
          </div>
        </div>

        {/* 低库存预警 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex justify-between items-center">
            <h3 className="text-lg font-bold">
              {language === 'en' ? 'Low Stock Alert' :
               language === 'km' ? 'ការព្រមានស្តុកទាប' :
               '低库存预警'}
            </h3>
            {lowStockProducts.length > 0 && (
              <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                {lowStockProducts.length}
              </span>
            )}
          </div>
          <div className="p-6">
            {lowStockProducts.length > 0 ? (
              <div className="space-y-4">
                {lowStockProducts.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium">{getProductName(product)}</p>
                        <p className="text-sm text-gray-500">
                          {language === 'en' ? 'Min stock' : language === 'km' ? 'ស្តុកអប្បបរមា' : '最低库存'}: {product.min_stock || 0}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">
                        {product.stock || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        {language === 'en' ? 'in stock' : language === 'km' ? 'នៅក្នុងស្តុក' : '库存'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-green-600 font-medium">
                  {language === 'en' ? '✓ All products well stocked' :
                   language === 'km' ? '✓ ផលិតផលទាំងអស់មានស្តុកគ្រប់គ្រាន់' :
                   '✓ 所有商品库存充足'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;